import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useMonitor } from "../../../contexts/MonitorContext";
import { Heading, Subheading } from "../../../design/heading";
import { Text } from "../../../design/text";
import { Badge } from "../../../design/badge";
import { Button } from "../../../design/button";
import { Visit, VisitStatus, Event, EventType } from "@visitproof/types";
import { VisitService } from "../../../services/VisitService";
import { EventService } from "../../../services/EventService";
import moment from "moment";
import { recordWebTelemetry } from "../../../utils/telemetry";
import {
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import CreateEventDialog from "./components/CreateEventDialog";
import EventCard from "./components/EventCard";

const getStatusBadge = (status: VisitStatus) => {
  switch (status) {
    case "scheduled":
      return <Badge color="blue">Scheduled</Badge>;
    case "supervisor_enroute":
      return <Badge color="yellow">En Route</Badge>;
    case "supervisor_arrived":
      return <Badge color="orange">Arrived</Badge>;
    case "supervisor_started":
      return <Badge color="green">In Progress</Badge>;
    case "supervisor_ended":
    case "completed":
      return <Badge color="zinc">Completed</Badge>;
    case "canceled":
      return <Badge color="red">Canceled</Badge>;
    case "supervisor_no_show":
      return <Badge color="red">No Show</Badge>;
    default:
      return <Badge color="zinc">{status}</Badge>;
  }
};

const MonitorVisitDetail: React.FC = () => {
  const { visitId } = useParams<{ visitId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { supervisor } = useMonitor();

  const [visit, setVisit] = useState<Visit | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateEventOpen, setIsCreateEventOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const loadVisitAndEvents = useCallback(async () => {
    if (!visitId || !supervisor?.accountId) return;

    try {
      setLoading(true);
      // First fetch the visit
      const visitData = await VisitService.getVisit(visitId);
      setVisit(visitData);

      // Then fetch events — scoped to THIS supervisor's id so the query
      // satisfies the events security rule (a supervisor may only read events
      // for their own supervisorId). Using the unscoped query here caused the
      // "Missing or insufficient permissions" error when opening a visit.
      if (visitData) {
        const eventsData = await EventService.getEventsByVisitForSupervisor(
          visitId,
          supervisor.accountId,
          supervisor.id
        );
        setEvents(eventsData);
      }
    } catch (err: any) {
      console.error("Error loading visit:", err);
      setError(err instanceof Error ? err.message : "Failed to load visit");
      recordWebTelemetry("error", "monitor_open_visit_failed", {
        code: err?.code || "unknown",
        message: err?.message?.slice(0, 200),
        visitId,
      });
    } finally {
      setLoading(false);
    }
  }, [visitId, supervisor?.accountId]);

  useEffect(() => {
    loadVisitAndEvents();
  }, [loadVisitAndEvents]);

  const handleStatusUpdate = async (newStatus: VisitStatus, eventType: EventType) => {
    if (!visit || !user) return;

    try {
      setUpdatingStatus(true);

      // Create the status event. supervisorId is REQUIRED for the events
      // create rule to allow a monitor's write — without it the write is
      // denied for supervisors (admins bypass the check).
      await EventService.createEvent({
        type: eventType,
        visitId: visit.id,
        caseId: visit.caseId,
        accountId: visit.accountId!,
        createdBy: user.uid,
        supervisorId: visit.supervisorId,
      });

      // Update visit status
      await VisitService.updateVisit(visit.id, { status: newStatus });

      // Reload data
      await loadVisitAndEvents();
    } catch (err: any) {
      console.error("Error updating status:", err);
      setError(err instanceof Error ? err.message : "Failed to update status");
      recordWebTelemetry("error", "monitor_save_event_failed", {
        code: err?.code || "unknown",
        message: err?.message?.slice(0, 200),
        visitId: visit.id,
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleEventCreated = () => {
    loadVisitAndEvents();
    setIsCreateEventOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Text>Loading...</Text>
      </div>
    );
  }

  if (error || !visit) {
    return (
      <div className="p-6">
        <Button plain onClick={() => navigate("/monitor/visits")}>
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Visits
        </Button>
        <div className="p-4 mt-4 rounded-lg bg-red-50 dark:bg-red-900/20">
          <Text className="text-red-600 dark:text-red-400">
            {error || "Visit not found"}
          </Text>
        </div>
      </div>
    );
  }

  const startTime = moment(visit.startDate.toDate());
  const endTime = moment(visit.endDate.toDate());
  const isActive = ["supervisor_enroute", "supervisor_arrived", "supervisor_started"].includes(visit.status);
  const isCompleted = ["supervisor_ended", "completed"].includes(visit.status);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Button plain onClick={() => navigate("/monitor/visits")} className="mb-4">
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Visits
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <Heading>Visit Details</Heading>
            <Text className="mt-1 text-zinc-600 dark:text-zinc-400">
              {startTime.format("dddd, MMMM D, YYYY")}
            </Text>
          </div>
          {getStatusBadge(visit.status)}
        </div>
      </div>

      {/* Visit Info Card */}
      <div className="p-6 mb-6 border rounded-lg border-zinc-200 dark:border-zinc-700">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-5 h-5 text-zinc-500" />
            <div>
              <Text className="text-sm text-zinc-500">Date</Text>
              <Text className="font-medium">{startTime.format("MMMM D, YYYY")}</Text>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <ClockIcon className="w-5 h-5 text-zinc-500" />
            <div>
              <Text className="text-sm text-zinc-500">Time</Text>
              <Text className="font-medium">
                {startTime.format("h:mm A")} - {endTime.format("h:mm A")}
              </Text>
            </div>
          </div>

          {visit.location && (
            <div className="flex items-center gap-3 md:col-span-2">
              <MapPinIcon className="w-5 h-5 text-zinc-500" />
              <div>
                <Text className="text-sm text-zinc-500">Location</Text>
                <Text className="font-medium">
                  {visit.location.name || visit.location.formattedAddress}
                </Text>
              </div>
            </div>
          )}
        </div>

        {visit.notes && (
          <div className="pt-4 mt-4 border-t border-zinc-200 dark:border-zinc-700">
            <Text className="text-sm text-zinc-500">Notes</Text>
            <Text className="mt-1">{visit.notes}</Text>
          </div>
        )}
      </div>

      {/* Status Actions */}
      {!isCompleted && visit.status !== "canceled" && (
        <div className="p-4 mb-6 border rounded-lg border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800">
          <Subheading className="mb-3">Update Status</Subheading>
          <div className="flex flex-wrap gap-3">
            {visit.status === "scheduled" && (
              <Button
                color="orange"
                onClick={() => handleStatusUpdate("supervisor_arrived", "supervisor_arrived")}
                disabled={updatingStatus}
              >
                {updatingStatus ? "Updating..." : "I've Arrived"}
              </Button>
            )}

            {visit.status === "supervisor_arrived" && (
              <>
                <Button
                  color="green"
                  onClick={() => handleStatusUpdate("supervisor_started", "supervisor_started")}
                  disabled={updatingStatus}
                >
                  {updatingStatus ? "Updating..." : "Start Visit"}
                </Button>
                <Button
                  color="red"
                  onClick={() => handleStatusUpdate("supervisor_no_show", "supervisor_no_show")}
                  disabled={updatingStatus}
                >
                  {updatingStatus ? "Updating..." : "No Show"}
                </Button>
              </>
            )}

            {visit.status === "supervisor_started" && (
              <Button
                color="zinc"
                onClick={() => handleStatusUpdate("supervisor_ended", "supervisor_ended")}
                disabled={updatingStatus}
              >
                {updatingStatus ? "Updating..." : "End Visit"}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Complete Documentation - shown after visit has ended */}
      {visit.status === "supervisor_ended" && (
        <div className="p-4 mb-6 border rounded-lg border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
          <Subheading className="mb-2">Documentation in Progress</Subheading>
          <Text className="mb-3 text-sm text-zinc-600 dark:text-zinc-400">
            The visit has ended. You can continue adding events and notes below. When you are finished documenting, click "Complete Documentation" to generate the report.
          </Text>
          <Button
            color="green"
            onClick={async () => {
              if (!visit) return;
              try {
                setUpdatingStatus(true);
                await VisitService.updateVisit(visit.id, { status: "completed" });
                await loadVisitAndEvents();
              } catch (err) {
                console.error("Error completing documentation:", err);
                setError(err instanceof Error ? err.message : "Failed to complete documentation");
              } finally {
                setUpdatingStatus(false);
              }
            }}
            disabled={updatingStatus}
          >
            {updatingStatus ? "Completing..." : "Complete Documentation"}
          </Button>
        </div>
      )}

      {/* Events Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <Subheading>Events ({events.length})</Subheading>
          {(isActive || isCompleted) && (
            <Button color="blue" onClick={() => setIsCreateEventOpen(true)}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          )}
        </div>

        {events.length === 0 ? (
          <div className="p-6 text-center border rounded-lg border-zinc-200 dark:border-zinc-700">
            <Text className="text-zinc-500">No events recorded yet</Text>
            {(isActive || isCompleted) && (
              <Button
                color="blue"
                className="mt-3"
                onClick={() => setIsCreateEventOpen(true)}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add First Event
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event) => (
              <EventCard key={event.id} event={event} onEventUpdated={loadVisitAndEvents} />
            ))}
          </div>
        )}
      </div>

      {/* Create Event Dialog */}
      <CreateEventDialog
        isOpen={isCreateEventOpen}
        onClose={() => setIsCreateEventOpen(false)}
        onEventCreated={handleEventCreated}
        visit={visit}
      />
    </div>
  );
};

export default MonitorVisitDetail;
